import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { EasTableWithoutFilter } from 'ui/EasTableWithoutFilter';
import { ItemWithId } from 'ui/EasTable/types';
import { TEmKeycloakUserItem } from 'redux/reducers/accessManagement/types';
import {
	getEmKeycloakUsers,
	getEmKeycloakUsersLoading,
	getResetUserPasswordLoading,
	getSetUserEnabledLoading,
	getAttachEmUserLoading,
	getDetachEmUserLoading,
	getCreateEmUserLoading,
	getEmKeycloakUsersFailure,
} from 'redux/reducers/accessManagement/selectors';
import { useEffect, useState } from 'react';
import {
	detachEmUserRequest,
	getEmKeycloakUsersRequest,
	resetKeycloakUserPasswordRequest,
	setKeycloakUserEnabledRequest,
} from 'redux/reducers/accessManagement/reducer';
import { useParams } from 'react-router-dom';
import { ConfirmationDialog } from 'ui/ConfirmationDialog';
import { HelpButton } from 'ui/HelpButton';
import { boxStructure, tableStructure } from './tableStructure';
import { AddUseDialog } from './AddUseDialog';
import { COLORS } from '../../../../../services/constants/colors';
import { EHelpButtonArticles } from '../../../../../ui/HelpButton/constants';
import { EditParamsDialog } from '../../../../ReferenceTables/AdditionalServices/EditParamsDialog';
import { TEmNotificationItem } from '../../../../../redux/reducers/emManagement/types';
import {
	getEmDashboardUsersSubscriptionsData,
	getEmNotifications,
	getUpdateEmDashboardUsersSubscriptionsLoading,
} from '../../../../../redux/reducers/emManagement/selectors';
import {
	getEmDashboardUsersSubscriptionsRequest,
	getEmNotificationsRequest,
	updateEmDashboardUsersSubscriptionsRequest,
} from '../../../../../redux/reducers/emManagement/reducer';

export const EmAccessManagement = () => {
	const { emId } = useParams();
	const dispatch = useDispatch();
	const keycloakUsers = useSelector(getEmKeycloakUsers);
	const keycloakUsersLoading = useSelector(getEmKeycloakUsersLoading);
	const keycloakUsersFailure = useSelector(getEmKeycloakUsersFailure);
	const emDashboardUsersSubscriptions = useSelector(getEmDashboardUsersSubscriptionsData);
	const resetUserPasswordLoading = useSelector(getResetUserPasswordLoading);
	const updateEmDashboardUsersSubscriptionsLoading = useSelector(
		getUpdateEmDashboardUsersSubscriptionsLoading,
	);

	const setUserEnabledLoading = useSelector(getSetUserEnabledLoading);
	const attachEmUserLoading = useSelector(getAttachEmUserLoading);
	const detachEmUserLoading = useSelector(getDetachEmUserLoading);
	const createEmUserLoading = useSelector(getCreateEmUserLoading);
	const [openResetPasswordDialog, setOpenResetPasswordDialog] = useState(false);
	const [idToEdit, setIdToEdit] = useState<number | null>(null);
	const [openDetachUserDialog, setOpenDetachUserDialog] = useState(false);
	const [openEnableUserDialog, setOpenEnableUserDialog] = useState(false);
	const [openAddUserDialog, setOpenAddUserDialog] = useState(false);
	const [openEditNotificationSettingsDialog, setOpenEditNotificationSettingsDialog] =
		useState(false);
	const setCloseEditNotificationSettingsDialog = () => {
		setOpenEditNotificationSettingsDialog(false);
	};

	const setCloseResetPasswordDialog = () => {
		setOpenResetPasswordDialog(false);
	};
	const setCloseAddUserDialog = () => {
		setOpenAddUserDialog(false);
	};
	const handleResetPasswordDialog = () => {
		if (!idToEdit) return;
		setOpenResetPasswordDialog(false);
		dispatch(
			resetKeycloakUserPasswordRequest({
				email: keycloakUsers?.data?.find((item) => item.id === idToEdit)?.email ?? '',
			}),
		);
	};
	const setCloseEnableUserDialog = () => {
		setOpenEnableUserDialog(false);
	};
	const handleEnableUserDialog = () => {
		if (!idToEdit) return;
		setOpenEnableUserDialog(false);
		dispatch(
			setKeycloakUserEnabledRequest({
				body: {
					email: keycloakUsers?.data?.find((item) => item.id === idToEdit)?.email ?? '',
					emId: Number(emId),
				},
				getEmKeycloakUsers: { emId: Number(emId), pageNumber: 1, pageSize: 100 },
			}),
		);
	};
	const setCloseDetachUserDialog = () => {
		setOpenDetachUserDialog(false);
	};
	const handleDetachUserDialog = () => {
		if (!idToEdit) return;
		setOpenDetachUserDialog(false);
		dispatch(
			detachEmUserRequest({
				body: {
					email: keycloakUsers?.data?.find((item) => item.id === idToEdit)?.email ?? '',
					emId: Number(emId),
				},
				getEmKeycloakUsers: { emId: Number(emId), pageNumber: 1, pageSize: 100 },
			}),
		);
	};
	useEffect(() => {
		if (emId) {
			dispatch(getEmKeycloakUsersRequest({ emId: Number(emId), pageNumber: 1, pageSize: 100 }));
		}
	}, [dispatch, emId]);

	const handleAction = (id: number, action: string) => {
		if (action === 'Reset password') {
			setIdToEdit(id);
			setOpenResetPasswordDialog(true);
		}
		if (action === 'Detach') {
			setIdToEdit(id);
			setOpenDetachUserDialog(true);
		}
		if (action === 'Enable') {
			setIdToEdit(id);
			setOpenEnableUserDialog(true);
		}
		if (action === 'Change notification settings') {
			setIdToEdit(id);
			dispatch(
				getEmDashboardUsersSubscriptionsRequest({
					emId: Number(emId),
					email: keycloakUsers?.data?.find((user) => user.id === id)?.email ?? '',
				}),
			);

			setOpenEditNotificationSettingsDialog(true);
		}
	};

	const handleSaveNotificationSettings = (data: Record<string, any>) => {
		dispatch(
			updateEmDashboardUsersSubscriptionsRequest({
				emId: Number(emId),
				data: {
					email: keycloakUsers?.data?.find((user) => user.id === idToEdit)?.email ?? '',
					firstName: keycloakUsers?.data?.find((user) => user.id === idToEdit)?.firstName ?? '',
					lastName: keycloakUsers?.data?.find((user) => user.id === idToEdit)?.lastName ?? '',
					filedIossOss: data.filedIossOss,
					lodgeTheOssQuarterlyReport: data.lodgeTheOssQuarterlyReport,
					lodgeTheIossReport: data.lodgeTheIossReport,
					invoice: data.invoice,
					outstandingInvoice: data.outstandingInvoice,
					updateOnVatCorrectionStatus: data.updateOnVatCorrectionStatus,
					directMessage: data.directMessage,
				},
			}),
		);
	};
	return (
		<Box>
			<EasTableWithoutFilter<TEmKeycloakUserItem & ItemWithId>
				tableStructure={tableStructure}
				items={keycloakUsers}
				itemsIsLoading={
					keycloakUsersLoading ||
					resetUserPasswordLoading ||
					setUserEnabledLoading ||
					attachEmUserLoading ||
					detachEmUserLoading ||
					createEmUserLoading
				}
				handleAction={handleAction}
				skeletonRows={[1]}
			>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<Typography variant="body1" color={COLORS.PRIMARY} fontWeight="bold">
						Access Management
					</Typography>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						{(keycloakUsersLoading ||
							createEmUserLoading ||
							setUserEnabledLoading ||
							attachEmUserLoading ||
							detachEmUserLoading ||
							resetUserPasswordLoading) && <CircularProgress size={20} />}
						<Button
							variant="contained"
							color="primary"
							size="small"
							onClick={() => {
								setOpenAddUserDialog(true);
							}}
							disabled={keycloakUsersFailure}
						>
							Add User
						</Button>
						<HelpButton type={EHelpButtonArticles.EM_USERS} />
					</Box>
				</Box>
			</EasTableWithoutFilter>
			<ConfirmationDialog
				open={openResetPasswordDialog}
				setClose={setCloseResetPasswordDialog}
				handleConfirm={handleResetPasswordDialog}
				title="Reset Password"
				text="Are you sure you want to reset the password?"
			/>
			<ConfirmationDialog
				open={openDetachUserDialog}
				setClose={setCloseDetachUserDialog}
				handleConfirm={handleDetachUserDialog}
				title="Detach User"
				text="Are you sure you want to detach the user?"
			/>
			<ConfirmationDialog
				open={openEnableUserDialog}
				setClose={setCloseEnableUserDialog}
				handleConfirm={handleEnableUserDialog}
				title="Enable User"
				text="Are you sure you want to enable the user?"
			/>
			<AddUseDialog open={openAddUserDialog} setClose={setCloseAddUserDialog} />
			<EditParamsDialog<TEmNotificationItem>
				open={openEditNotificationSettingsDialog}
				setClose={setCloseEditNotificationSettingsDialog}
				data={emDashboardUsersSubscriptions ?? null}
				boxStructure={boxStructure}
				onSave={handleSaveNotificationSettings}
				title="Edit Notification Settings"
			/>
		</Box>
	);
};
